/**
 * Clue selection policy.
 *
 * Two modes:
 *  - tag drill: clues restricted to one tag, scheduled by FSRS.
 *  - adaptive ("all categories"): a weakness-targeting bandit picks a TAG, then
 *    FSRS picks the clue within it. This is the core of "help me strengthen the
 *    areas where I am weak."
 *
 * Within either mode the per-clue priority cascade (borrowed in spirit from
 * cert-pepper) is:
 *   1. Overdue review cards (due in the past), most-overdue first.
 *   2. Otherwise an unseen clue — chosen to cover the weak/under-sampled tags.
 *   3. Otherwise the least-recently-seen card (keeps things moving).
 *
 * Tag choice in adaptive mode is an epsilon-greedy bandit over weaknessPriority:
 * usually drill the weakest tags, but with probability EPSILON explore broadly
 * so new material and rarely-seen tags still surface.
 */
import type { CardState, Clue, TagSkill } from './types';
import { weaknessPriority } from './tagSkill';

export interface SelectionInputs {
  /** Candidate clue pool (already filtered to mode, e.g. one tag or all). */
  pool: Clue[];
  /** Card states keyed by clueId; absent => unseen. */
  cards: Map<number, CardState>;
  /** Tag skills keyed by tagId. */
  skills: Map<number, TagSkill>;
  now: number;
  /** Clue ids seen this session (avoid immediate repeats). */
  sessionSeen: Set<number>;
  /** Deterministic RNG in [0,1) for testability. */
  rng: () => number;
  /** Exploration rate for adaptive tag choice. */
  epsilon?: number;
}

const EPSILON_DEFAULT = 0.18;
/** Offset below skill targeted for ~80% predicted success (motivation > 50%). */
const DESIRABLE_DIFFICULTY_OFFSET = 0.35;

/** Weighted random index into `weights` (must be non-negative, sum > 0). */
function weightedPick(weights: number[], rng: () => number): number {
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return Math.floor(rng() * weights.length);
  let x = rng() * total;
  for (let i = 0; i < weights.length; i++) {
    x -= weights[i]!;
    if (x <= 0) return i;
  }
  return weights.length - 1;
}

/**
 * Pick the next clue. Returns null only if the pool is empty.
 *
 * `targetTags` constrains adaptive tag choice (the set of tags present in the
 * pool); pass the single tag for tag-drill mode.
 */
export function selectNextClue(inp: SelectionInputs): Clue | null {
  const { pool, cards, now, sessionSeen, rng } = inp;
  if (pool.length === 0) return null;

  const fresh = pool.filter((c) => !sessionSeen.has(c.i));
  const candidates = fresh.length > 0 ? fresh : pool;

  // 1. Overdue review cards (most overdue first).
  const overdue = candidates
    .filter((c) => {
      const st = cards.get(c.i);
      return st && st.due <= now;
    })
    .sort((a, b) => (cards.get(a.i)!.due - cards.get(b.i)!.due));
  if (overdue.length > 0) return overdue[0]!;

  // Partition into unseen vs seen-not-due.
  const unseen = candidates.filter((c) => !cards.has(c.i));
  const seen = candidates.filter((c) => cards.has(c.i));

  // Decide whether to serve unseen material this turn. Bias toward unseen while
  // the pool is largely unexplored, but always leave room for reviews.
  const wantUnseen = unseen.length > 0 && (seen.length === 0 || rng() < 0.6);
  const drawFrom = wantUnseen ? unseen : seen.length > 0 ? seen : unseen;

  return pickWeightedByTagWeakness(drawFrom, inp);
}

/**
 * Among `clues`, prefer those whose tags the user is weak at (or hasn't seen),
 * with epsilon-greedy exploration. Falls back to least-recently-seen ordering
 * for the seen pool.
 */
function pickWeightedByTagWeakness(clues: Clue[], inp: SelectionInputs): Clue | null {
  if (clues.length === 0) return null;
  const { skills, rng, cards } = inp;
  const epsilon = inp.epsilon ?? EPSILON_DEFAULT;

  // Exploration: uniform among the least-recently-seen half.
  if (rng() < epsilon) {
    const byRecency = [...clues].sort((a, b) => {
      const la = cards.get(a.i)?.lastReview ?? 0;
      const lb = cards.get(b.i)?.lastReview ?? 0;
      return la - lb;
    });
    const half = byRecency.slice(0, Math.max(1, Math.ceil(byRecency.length / 2)));
    return half[Math.floor(rng() * half.length)]!;
  }

  // Exploitation: weight each clue by the max weakness across its tags, scaled
  // down by clue difficulty so we don't only serve impossibly hard clues in a
  // weak tag — we want winnable practice slightly above current skill.
  const weights = clues.map((c) => {
    let tagWeak = 0.5;
    for (const t of c.t) {
      const sk = skills.get(t);
      const w = sk ? weaknessPriority(sk) : 1; // unseen tag => max priority
      if (w > tagWeak) tagWeak = w;
    }
    // Desirable difficulty: target a HIGH predicted success rate (~80%), not
    // the information-optimal 50% used in computer-adaptive *testing*. Adaptive-
    // learning research (Pelanek; Math Garden) shows practice apps motivate best
    // when most items are winnable, so we aim at clues somewhat *below* current
    // skill. With the logistic spread of 4 in expectedScore, p=0.8 implies a
    // difficulty offset of ~ln(0.25)/4 ≈ 0.35 below skill.
    const skillEst = bestTagSkill(c, inp);
    const target = Math.max(0, skillEst - DESIRABLE_DIFFICULTY_OFFSET);
    const fit = 1 - Math.min(1, Math.abs(c.df - target));
    // Square the weakness so the bandit commits decisively to weak tags rather
    // than spreading thinly; the difficulty fit still modulates within a tag.
    return tagWeak * tagWeak * (0.5 + 0.5 * fit);
  });

  return clues[weightedPick(weights, rng)]!;
}

function bestTagSkill(c: Clue, inp: SelectionInputs): number {
  let best = 0.5;
  let found = false;
  for (const t of c.t) {
    const sk = inp.skills.get(t);
    if (sk) {
      best = found ? Math.min(best, sk.rating) : sk.rating;
      found = true;
    }
  }
  return found ? best : 0.5;
}

/**
 * Rank tags by how much the user should drill them right now. Used by the
 * "weak areas" dashboard and to seed adaptive sessions.
 */
export function rankWeakTags(
  skills: Map<number, TagSkill>,
  minAttempts = 3,
): { tagId: number; priority: number; rating: number; attempts: number }[] {
  return [...skills.values()]
    .filter((s) => s.attempts >= minAttempts)
    .map((s) => ({
      tagId: s.tagId,
      priority: weaknessPriority(s),
      rating: s.rating,
      attempts: s.attempts,
    }))
    .sort((a, b) => b.priority - a.priority);
}
